import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import { useAuth } from './AuthContext';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const EmployeeList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/employees');
      if (response.data?.employees) {
        setEmployees(response.data.employees);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employees. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Filter employees based on search and status
  useEffect(() => {
    let filtered = [...employees];

    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(emp => emp.is_active !== false);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(emp => emp.is_active === false);
    }

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(emp =>
        emp.name?.toLowerCase().includes(search) ||
        emp.email?.toLowerCase().includes(search) ||
        emp.role?.toLowerCase().includes(search)
      );
    }

    setFilteredEmployees(filtered);
    setPage(0); // Reset to first page when filters change
  }, [employees, searchTerm, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (employee) => {
    navigate(`/employee_card?search=${encodeURIComponent(employee.name)}`);
  };

  const handleAddEmployee = () => {
    navigate('/employee_card?mode=new');
  };

  const formatRole = (role) => {
    if (!role) return 'Not Set';
    return role
      .replace(/_/g, ' ')
      .replace(/ACCOUNT /i, '')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getRoleColor = (role) => {
    if (!role) return 'default';
    const r = role.toUpperCase();
    if (r.includes('OWNER')) return 'primary';
    if (r.includes('MANAGER')) return 'secondary';
    return 'default';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name) => {
    if (!name) return '#9e9e9e';
    const colors = [
      '#2196F3', '#4CAF50', '#FF9800', '#9C27B0', 
      '#E91E63', '#00BCD4', '#FF5722', '#3F51B5'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Paginated data
  const paginatedEmployees = filteredEmployees.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #2196F3, #1976D2)',
          color: 'white',
          p: 4,
          mb: 4,
          borderRadius: 0
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<HomeIcon />}
            onClick={() => navigate('/team')}
            sx={{ 
              color: 'white', 
              mb: 2, 
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Back to Team Management
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              width: 56, 
              height: 56, 
              bgcolor: 'rgba(255,255,255,0.2)'
            }}>
              <PeopleIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                Employee Directory
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {employees.length} team member{employees.length !== 1 ? 's' : ''} in your salon
              </Typography>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="lg">
        {/* Action Bar */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 3, 
            border: '1px solid #e0e0e0',
            borderRadius: 2
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
              {/* Search */}
              <TextField
                size="small"
                placeholder="Search by name, email, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ minWidth: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
              />

              {/* Status Filter */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterIcon color="action" fontSize="small" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="all">All Staff</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                  <MenuItem value="inactive">Inactive Only</MenuItem>
                </Select>
              </FormControl>

              {/* Refresh */}
              <Tooltip title="Refresh list">
                <IconButton onClick={fetchEmployees} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Add Button */}
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleAddEmployee}
              sx={{
                background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                textTransform: 'none',
                fontWeight: 600,
                px: 3
              }}
            >
              Add Employee
            </Button>
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Employee Table */}
        <Paper 
          elevation={0} 
          sx={{ 
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              py: 8
            }}>
              <CircularProgress />
            </Box>
          ) : filteredEmployees.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              color: 'text.secondary'
            }}>
              <PeopleIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {searchTerm || statusFilter !== 'all' 
                  ? 'No employees match your filters' 
                  : 'No employees yet'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Get started by adding your first team member'}
              </Typography>
              {!searchTerm && statusFilter === 'all' && (
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleAddEmployee}
                >
                  Add Your First Employee
                </Button>
              )}
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                        Employee
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '25%' }}>
                        Contact
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '15%' }}>
                        Role
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '10%' }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '15%' }} align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedEmployees.map((employee) => (
                      <TableRow
                        key={employee.id}
                        hover
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f5f8ff' }
                        }}
                        onClick={() => handleRowClick(employee)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar 
                              sx={{ 
                                bgcolor: getAvatarColor(employee.name),
                                fontWeight: 600
                              }}
                            >
                              {getInitials(employee.name)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {employee.name || 'Unnamed'}
                              </Typography>
                              {employee.start_date && (
                                <Typography variant="caption" color="text.secondary">
                                  Since {new Date(employee.start_date).toLocaleDateString('en-GB', {
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {employee.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                                  {employee.email}
                                </Typography>
                              </Box>
                            )}
                            {employee.phone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  {employee.phone}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatRole(employee.role)}
                            size="small"
                            color={getRoleColor(employee.role)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={employee.is_active !== false ? 'Active' : 'Inactive'}
                            size="small"
                            color={employee.is_active !== false ? 'success' : 'default'}
                            sx={{ 
                              fontWeight: 500,
                              ...(employee.is_active === false && {
                                bgcolor: '#f5f5f5',
                                color: '#9e9e9e'
                              })
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(employee);
                                }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Employee">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/employee_card?search=${encodeURIComponent(employee.name)}&mode=edit`);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                component="div"
                count={filteredEmployees.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{ borderTop: '1px solid #e0e0e0' }}
              />
            </>
          )}
        </Paper>

        {/* Summary Stats */}
        {!loading && employees.length > 0 && (
          <Box sx={{ 
            mt: 3, 
            display: 'flex', 
            gap: 3, 
            justifyContent: 'center',
            color: 'text.secondary'
          }}>
            <Typography variant="body2">
              <strong>{employees.filter(e => e.is_active !== false).length}</strong> active
            </Typography>
            <Typography variant="body2">•</Typography>
            <Typography variant="body2">
              <strong>{employees.filter(e => e.is_active === false).length}</strong> inactive
            </Typography>
            <Typography variant="body2">•</Typography>
            <Typography variant="body2">
              <strong>{employees.length}</strong> total
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default EmployeeList;